import * as React from 'react';
import BPromise from 'bluebird';
import Lazy from 'lazy.js';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import {
  ColumnDefinition,
  getFilters2,
  getPagesCount,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import {
  getClubDays,
  getClubFinishDateFromMessage,
  getClubMessageStatus,
  getIsParticipant,
  getClubStartDateFromMessage,
  getClubStartTimeFromMessage,
  getUpdatedAt
} from 'Src/helpers/accessor/accessor';
import { ClubMessage } from 'Src/models/clubMessage';
import {
  getSelectOptionsForClubDays,
  getSelectOptionsForClubPriority,
  getSelectOptionsForClubMessagesStatus,
  getSelectOptionForBooleanQuestion
} from 'Src/helpers/table/select';
import {
  getAllSchoolClubMessages,
  getSchoolClubMessages,
  getSchoolClubMessagesCount
} from 'Src/helpers/service/admin/schoolClubMessages';

import '../../Invites/SchoolInvitesHub.scss';
import './Invitations.css';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { addClubStudents, updateClubMessage } from 'Src/helpers/service/admin/clubStudents';
import { ClubMessageStatusForm } from './ClubMessageStatusForm/ClubMessageStatusForm';
import { CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS } from 'Src/consts/message';
import { ClubConfirmationRequestSummary } from '../Clubs/ClubConfirmationRequestSummary/ClubConfirmationRequestSummary';
import {
  getRespondedAtText,
  getResponseUserText,
  statusChangedAfterFirstResponse,
  wasEditedAfterResponse
} from 'Src/helpers/message/message';
import '../Clubs/Clubs.css';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';

const INVITATIONS_COLUMNS_VIEW_KEY = 'clubs.invitations';
const INVITATIONS_VIEW_QUERY_KEY = 'invitationView';

type InvitationsView = 'current' | 'archive';

const getCurrentAcademicYearBounds = (): { from: string; to: string } => {
  const today = moment();
  const academicYearStartYear = today.month() >= 8 ? today.year() : today.year() - 1;

  return {
    from: moment.utc([academicYearStartYear, 8, 1]).startOf('day').toISOString(),
    to: moment
      .utc([academicYearStartYear + 1, 7, 31])
      .endOf('day')
      .toISOString()
  };
};

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Student search',
    field: 'studentSearch',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'firstName']
  },
  {
    text: 'Club name',
    field: 'name',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['clubData', 'name']
  },
  {
    text: 'Start time',
    field: 'startTime',
    isSort: false,
    type: FILTER_TYPE.TIME_INTERVAL,
    accessor: getClubStartTimeFromMessage
  },
  {
    text: 'Start date',
    field: 'startDate',
    isSort: false,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getClubStartDateFromMessage
  },
  {
    text: 'Finish date',
    field: 'finishDate',
    isSort: false,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getClubFinishDateFromMessage
  },
  {
    text: 'Days',
    field: 'days',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getClubDays
  },
  {
    text: 'Name',
    field: 'firstName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'firstName']
  },
  {
    text: 'Surname',
    field: 'lastName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['playerDetailsData', 'lastName']
  },
  {
    text: 'Updated',
    field: 'updatedAt',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: getUpdatedAt
  },
  {
    text: 'Invitation status',
    field: 'invitationStatus',
    isSort: true,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getClubMessageStatus
  },
  {
    text: 'Priority',
    field: 'priority',
    isSort: true,
    accessor: ['priority'],
    type: FILTER_TYPE.SELECT
  },
  {
    text: 'Participant',
    field: 'participant',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getIsParticipant
  }
];

interface State {
  items: ClubMessage[];
  currentPage: number;
  selectedItems: ClubMessage[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;

  isClubErrorsModalOpen: boolean;
  errors: InvitationsError[];
  isClubInfoModalOpen: boolean;
  isChangeStatusModalOpen: boolean;
  previewItem?: ClubMessage;
  isColumnsMenuOpen: boolean;
  hiddenColumnKeys: string[];
}

interface InvitationsError {
  name: string;
  maxParticipants: number;
  participantsCount: number;
  potentialClubParticipantsCount: number;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Invitations extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',

      isClubErrorsModalOpen: false,
      errors: [],
      isClubInfoModalOpen: false,
      isChangeStatusModalOpen: false,
      previewItem: undefined,
      isColumnsMenuOpen: false,
      hiddenColumnKeys: loadHiddenColumns(props.user, INVITATIONS_COLUMNS_VIEW_KEY)
    };
  }

  componentDidMount() {
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail: 'Clubs / Invitations' }));
    this.setState({
      isLoading: true
    });
    this.setItems();
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  onDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (target?.closest?.('.clubsDsColumns')) return;
    if (this.state.isColumnsMenuOpen) this.setState({ isColumnsMenuOpen: false });
  };

  onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state.isColumnsMenuOpen) this.setState({ isColumnsMenuOpen: false });
  };

  setHiddenColumns = (hiddenColumnKeys: string[]): void => {
    saveHiddenColumns(this.props.user, INVITATIONS_COLUMNS_VIEW_KEY, hiddenColumnKeys);
    this.setState({ hiddenColumnKeys });
  };

  toggleColumn = (key: string): void => {
    const hiddenColumnKeys = this.state.hiddenColumnKeys.includes(key)
      ? this.state.hiddenColumnKeys.filter(field => field !== key)
      : [...this.state.hiddenColumnKeys, key];
    this.setHiddenColumns(hiddenColumnKeys);
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps);
    }
  }

  getInvitationsView = (searchValue: string = this.props.history.location.search): InvitationsView => {
    const search = parse(searchValue);
    return search[INVITATIONS_VIEW_QUERY_KEY] === 'archive' ? 'archive' : 'current';
  };

  getScopedWhere = (filters: any, view: InvitationsView = this.getInvitationsView()): any => {
    const where = getServerFieldSectionWhere(filters);

    // Keep the academic-year part of Current compatible with an API instance that has not yet picked up
    // the explicit view parameter. The Draft/Archive status split is enforced by the scoped backend query.
    if (view === 'current') {
      const academicYear = getCurrentAcademicYearBounds();
      const existingStartDate = where['schedule.startDate'] || {};
      const existingFrom = existingStartDate.$gte;
      const existingTo = existingStartDate.$lte;

      where['schedule.startDate'] = {
        ...existingStartDate,
        $gte:
          existingFrom && moment(existingFrom).isAfter(academicYear.from) ? existingFrom : academicYear.from,
        $lte: existingTo && moment(existingTo).isBefore(academicYear.to) ? existingTo : academicYear.to
      };
    }

    return where;
  };

  addViewToSearch = (search: string[], view: InvitationsView = this.getInvitationsView()): void => {
    if (view === 'archive') search.push(`${INVITATIONS_VIEW_QUERY_KEY}=archive`);
  };

  setItems(props: Props = this.props) {
    const { history, user } = props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;
    const view = this.getInvitationsView(history.location.search);

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isDataFiltered = isFilterExist2(filters);

    if (view === 'archive' && !isDataFiltered) {
      this.setState({
        currentPage: FIRST_PAGE,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: false,
        isDataFiltered: false,
        filters: filters,
        items: [],
        selectedItems: [],
        isSelectAllChecked: false,
        total: 0,
        basePath: history.location.pathname,
        isLoading: false
      });

      return BPromise.resolve(true);
    }

    const where = this.getScopedWhere(filters, view);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const pagePromise = BPromise.resolve(getSchoolClubMessages(user, serverQueryFilter, view)).then(response => {
      if (Array.isArray(response)) {
        return getSchoolClubMessagesCount(user, where, view).then(countObj => ({
          items: response,
          count: countObj.count
        }));
      }

      return {
        items: response && Array.isArray(response.items) ? response.items : [],
        count: Number(response && response.count) || 0
      };
    });

    return pagePromise.then(({ count, items }) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: false,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: count,
        basePath: history.location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    this.addViewToSearch(search);

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = this.getScopedWhere(filters);

    getAllSchoolClubMessages(user, where, this.getInvitationsView()).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    this.addViewToSearch(search);
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (options && options.modernMultiValue) {
      nextFilters = {
        ...nextFilters,
        [filterField]: filterValue
      };
    } else if (options && options.modernRange) {
      nextFilters = {
        ...nextFilters,
        [filterField]: options.modernRange
      };
    } else if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    this.addViewToSearch(search);

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    const search: string[] = [];
    this.addViewToSearch(search);

    this.setState({
      filters: getFilters2(COLUMNS, {}),
      selectedItems: [],
      isSelectAllChecked: false,
      isDataFiltered: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onInvitationsViewChange = (view: InvitationsView): void => {
    if (view === this.getInvitationsView()) return;

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      previewItem: undefined,
      isShowFilter: false,
      isColumnsMenuOpen: false,
      filters: getFilters2(COLUMNS, {})
    });

    this.props.history.push({
      pathname: this.state.basePath || this.props.history.location.pathname,
      search: view === 'archive' ? `${INVITATIONS_VIEW_QUERY_KEY}=archive` : ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    if (isShowFilter) {
      this.onCancelFilterClick();
      return;
    }

    this.setState({
      isShowFilter: true
    });
  };

  onCancelFilterClick = (): void => {
    const search = parse(this.props.history.location.search);
    this.setState({
      isShowFilter: false,
      filters: getFilters2(COLUMNS, search)
    });
  };

  getFilterDate(value: string): Date | null {
    if (!value) return null;
    const date = moment(value, 'DD-MM-YYYY', true);
    return date.isValid() ? date.toDate() : null;
  }

  setDateRangeFilter = (field: string, value: any): void => {
    this.setState({
      filters: {
        ...this.state.filters,
        [field]: {
          from: value && value.start ? moment(value.start).format('DD-MM-YYYY') : '',
          to: value && value.end ? moment(value.end).format('DD-MM-YYYY') : ''
        }
      }
    });
  };

  getFilterTime(value: string): Date | null {
    if (!value) return null;
    const time = moment(value, 'HH:mm', true);
    return time.isValid() ? time.toDate() : null;
  }

  setTimeRangeFilter = (side: 'from' | 'to', value: Date | null): void => {
    this.setState({
      filters: {
        ...this.state.filters,
        startTime: {
          ...this.state.filters.startTime,
          [side]: value ? moment(value).format('HH:mm') : ''
        }
      }
    });
  };

  removeAppliedFilter = (field: string): void => {
    const nextFilters = getFilters2(COLUMNS, parse(this.props.history.location.search));
    delete nextFilters[field];
    this.setState({ filters: nextFilters }, this.onApplyFilterClick);
  };

  onAddToClubClick = (): void => {
    const { selectedItems } = this.state;

    const potentialClubsParticipants = selectedItems.map(message => {
      return {
        userId: message.playerDetails.userId,
        permissionId: message.playerDetails.permissionId,
        clubId: message.clubId
      };
    });

    const clubIds = Lazy(selectedItems)
      .uniq('clubId')
      .map(message => message.clubId)
      .toArray();

    const errors = [];

    clubIds.forEach(clubId => {
      const potentialClubParticipants = potentialClubsParticipants.filter(participant => participant.clubId === clubId);
      const potentialClubParticipantsCount = potentialClubParticipants.length;

      const clubMessage = selectedItems.find(message => message.clubId === clubId);
      const { clubData } = clubMessage;
      const { maxParticipants, participants } = clubData;
      const participantsCount = participants.length;
      const totalParticipants = participantsCount + potentialClubParticipantsCount;
      const isUnlimitedMaxParticipants = maxParticipants === 0;

      if (!isUnlimitedMaxParticipants && maxParticipants < totalParticipants) {
        errors.push({
          name: clubData.name,
          maxParticipants: maxParticipants,
          participantsCount: participantsCount,
          potentialClubParticipantsCount: potentialClubParticipantsCount
        });
      }
    });

    if (errors.length > 0) {
      this.setState({
        isClubErrorsModalOpen: true,
        errors: errors
      });
    } else {
      this.setState({
        isClubInfoModalOpen: true
      });
    }
  };

  isAddToClubButtonActive(): boolean {
    const { selectedItems } = this.state;
    const isSomeSelectedItemsParticipant = selectedItems.some(message => message.isParticipant);
    return selectedItems.length !== 0 && !isSomeSelectedItemsParticipant;
  }

  changeStatus = () => {
    this.setState({
      isChangeStatusModalOpen: true
    });
  };

  onChangeStatusCancelClick = () => {
    this.setState({
      isChangeStatusModalOpen: false
    });
  };

  renderSchoolClubMessagesTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      selectedItems,
      filters,
      currentPage,
      total,
      previewItem
    } = this.state;
    const invitationColumns = [
      { key: 'club', label: 'Club', locked: true },
      { key: 'student', label: 'Student' },
      { key: 'schedule', label: 'Schedule' },
      { key: 'status', label: 'Status' },
      { key: 'priority', label: 'Priority' },
      { key: 'updated', label: 'Updated' },
      { key: 'participant', label: 'Participant' }
    ];
    const visible = (key: string) => !this.state.hiddenColumnKeys.includes(key);
    const visibleColumnCount = invitationColumns.filter(column => column.locked || visible(column.key)).length;
    const pageCount = Math.max(1, getPagesCount(total, LIMIT));
    const pageWindowStart = Math.max(1, currentPage - 2);
    const pageWindowEnd = Math.min(pageCount, pageWindowStart + 4);
    const pages = Array.from({ length: pageWindowEnd - pageWindowStart + 1 }, (_, index) => pageWindowStart + index);
    const allOnPageSelected = items.length > 0 && items.every(item => selectedItems.some(row => row.id === item.id));
    const statusOptions = getSelectOptionsForClubMessagesStatus(this.props.user);
    const dayOptions = getSelectOptionsForClubDays();
    const priorityOptions = getSelectOptionsForClubPriority().filter(option => option.value !== '');
    const participantOptions = getSelectOptionForBooleanQuestion();
    const statusValues = Array.isArray(filters.invitationStatus)
      ? filters.invitationStatus
      : filters.invitationStatus
      ? [filters.invitationStatus]
      : [];
    const appliedFilters = getFilters2(COLUMNS, parse(this.props.history.location.search));
    const invitationsView = this.getInvitationsView();
    const isArchiveView = invitationsView === 'archive';
    const isArchiveAwaitingFilters = isArchiveView && !isFilterExist2(appliedFilters);
    const academicYear = getCurrentAcademicYearBounds();
    const academicYearLabel = `${moment(academicYear.from).format('YYYY')}/${moment(academicYear.to).format('YY')}`;
    const appliedStatusValues = Array.isArray(appliedFilters.invitationStatus)
      ? appliedFilters.invitationStatus
      : appliedFilters.invitationStatus
      ? [appliedFilters.invitationStatus]
      : [];
    const getStatusClass = (message: ClubMessage) => {
      const status = String(message.invitationStatus || '').toUpperCase();
      if (status === 'ACCEPTED') return 'isSuccess';
      if (status === 'REJECTED' || status === 'OUTDATED') return 'isDanger';
      if (status === 'WAITING_LIST' || status === 'WAITING_LIST_CAN_BOOK') return 'isWarning';
      return 'isNeutral';
    };
    const formatTableDate = (value: string | Date | undefined) => {
      if (!value) return '—';
      const date = moment(value);
      return date.isValid() ? date.format('DD MMM YYYY') : '—';
    };
    const activeFilters: Array<{ field: string; label: string; value: string }> = [];
    if (appliedFilters.name) {
      activeFilters.push({ field: 'name', label: 'Club', value: appliedFilters.name });
    }
    if (appliedFilters.firstName) {
      activeFilters.push({ field: 'firstName', label: 'Name', value: appliedFilters.firstName });
    }
    if (appliedFilters.lastName) {
      activeFilters.push({ field: 'lastName', label: 'Surname', value: appliedFilters.lastName });
    }
    if (appliedFilters.studentSearch) {
      activeFilters.push({ field: 'studentSearch', label: 'Student', value: appliedFilters.studentSearch });
    }
    if (appliedStatusValues.length) {
      const labels = appliedStatusValues.map(value => {
        const option = statusOptions.find(item => String(item.value) === String(value));
        return String(option?.text || value);
      });
      activeFilters.push({ field: 'invitationStatus', label: 'Status', value: labels.join(', ') });
    }
    if ((appliedFilters.days || []).length) {
      const dayLabels = (appliedFilters.days || []).map(value => {
        const option = dayOptions.find(day => String(day.value) === String(value));
        return option?.text || value;
      });
      activeFilters.push({ field: 'days', label: 'Days', value: dayLabels.join(', ') });
    }
    if (appliedFilters.startTime?.from || appliedFilters.startTime?.to) {
      activeFilters.push({
        field: 'startTime',
        label: 'Start time',
        value: [appliedFilters.startTime.from, appliedFilters.startTime.to].filter(Boolean).join(' – ')
      });
    }
    if (appliedFilters.startDate?.from || appliedFilters.startDate?.to) {
      activeFilters.push({
        field: 'startDate',
        label: 'Start date',
        value: [appliedFilters.startDate.from, appliedFilters.startDate.to].filter(Boolean).join(' – ')
      });
    }
    if (appliedFilters.finishDate?.from || appliedFilters.finishDate?.to) {
      activeFilters.push({
        field: 'finishDate',
        label: 'Finish date',
        value: [appliedFilters.finishDate.from, appliedFilters.finishDate.to].filter(Boolean).join(' – ')
      });
    }
    if (appliedFilters.priority) {
      const option = priorityOptions.find(item => String(item.value) === String(appliedFilters.priority));
      activeFilters.push({ field: 'priority', label: 'Priority', value: String(option?.text || appliedFilters.priority) });
    }
    if (appliedFilters.participant !== '' && typeof appliedFilters.participant !== 'undefined') {
      const option = participantOptions.find(item => String(item.value) === String(appliedFilters.participant));
      activeFilters.push({
        field: 'participant',
        label: 'Participant',
        value: String(option?.text || appliedFilters.participant)
      });
    }

    return (
      <div className={`clubsDsPage clubsInvitationsDsPage ${previewItem ? 'hasDrawer' : ''}`}>
        <div className="clubsDsMain">
          <div className="clubsDsHeader">
            <div>
              <div className="clubsDsHeaderTitle">
                <h1>Club invitations</h1>
                {!isArchiveAwaitingFilters && <span>{total}</span>}
              </div>
            </div>
          </div>
          <section className="clubsDsContentCard clubsInvitationsContentCard">
            <div className="clubsInvitationsTabs" role="tablist" aria-label="Invitation lists">
              <button
                type="button"
                role="tab"
                aria-selected={!isArchiveView}
                className={!isArchiveView ? 'isActive' : ''}
                onClick={() => this.onInvitationsViewChange('current')}
              >
                Current
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isArchiveView}
                className={isArchiveView ? 'isActive' : ''}
                onClick={() => this.onInvitationsViewChange('archive')}
              >
                Archive
              </button>
              <span>
                {isArchiveView
                  ? 'Archived invitations are loaded only after filters are applied.'
                  : `Draft clubs planned for academic year ${academicYearLabel}.`}
              </span>
            </div>
            <div className="clubsDsToolbar">
            <div className="clubsDsToolbarTitle">
              <strong>{isArchiveView ? 'Archived invitations' : 'Current invitations'}</strong>
              {!isArchiveAwaitingFilters && <span className="clubsDsToolbarCount">{total}</span>}
            </div>
            <div className="clubsDsSearch">
              <input
                value={filters.name || ''}
                placeholder="Search by club name"
                onChange={event => this.onTableFilterChange(event, 'name')}
                onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
              />
            </div>
            <div className="clubsDsSearch">
              <input
                value={filters.studentSearch || ''}
                placeholder="Search student"
                onChange={event => this.onTableFilterChange(event, 'studentSearch')}
                onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
              />
            </div>
            <div className="clubsDsFilterAnchor">
              <button
                type="button"
                className={`clubsDsIconTextBtn ${isShowFilter ? 'isActive' : ''}`}
                onClick={this.onTableFilterClick}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  aria-hidden="true"
                >
                  <path d="M4 6h16M7 12h10M10 18h4" />
                </svg>
                Filters
              </button>
              {isShowFilter && (
                <>
                  <div className="clubsInvitationsFilterScrim" onClick={this.onCancelFilterClick} aria-hidden="true" />
                  <div
                    className="clubsDsFilters clubsInvitationsFilters"
                    onKeyDown={event => {
                      const target = event.target as HTMLElement;
                      if (event.key === 'Enter' && (target.tagName === 'INPUT' || target.tagName === 'SELECT')) {
                        event.preventDefault();
                        this.onApplyFilterClick();
                      }
                    }}
                  >
                    <header>
                      <div>
                        <strong>Filters</strong>
                      </div>
                      <button type="button" aria-label="Close filters" onClick={this.onCancelFilterClick}>
                        ×
                      </button>
                    </header>
                    <div className="clubsDsFilterBody">
                      <label className="clubsDsFilterField">
                        <span>Invitation status</span>
                        <div className="clubsDsFilterChips">
                          {statusOptions
                            .filter(option => option.value)
                            .map(option => {
                              const selected = statusValues.map(String).includes(String(option.value));
                              const next = selected
                                ? statusValues.filter(value => String(value) !== String(option.value))
                                : [...statusValues, option.value];
                              return (
                                <button
                                  type="button"
                                  key={String(option.value)}
                                  className={`clubsDsFilterChip ${selected ? 'isSelected' : ''}`}
                                  onClick={() =>
                                    this.onTableFilterChange({ target: { value: next } }, 'invitationStatus', {
                                      modernMultiValue: true
                                    })
                                  }
                                >
                                  {option.text}
                                </button>
                              );
                            })}
                        </div>
                      </label>
                      <div className="clubsDsFilterField">
                        <span>Days</span>
                        <div className="clubsDsFilterChips">
                          {dayOptions.map(option => {
                            const selected = (filters.days || []).map(String).includes(String(option.value));
                            const next = selected
                              ? (filters.days || []).filter(value => String(value) !== String(option.value))
                              : [...(filters.days || []), option.value];
                            return (
                              <button
                                type="button"
                                key={String(option.value)}
                                className={`clubsDsFilterChip ${selected ? 'isSelected' : ''}`}
                                onClick={() =>
                                  this.onTableFilterChange({ target: { value: next } }, 'days', {
                                    modernMultiValue: true
                                  })
                                }
                              >
                                {option.text}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <fieldset className="clubsDsFilterField clubsInvitationsFilterChips">
                        <legend>Priority</legend>
                        <div className="clubsDsFilterChips">
                          {priorityOptions.map(option => {
                            const selected = String(filters.priority || '') === String(option.value);
                            return (
                              <button
                                type="button"
                                key={String(option.value)}
                                className={`clubsDsFilterChip ${selected ? 'isSelected' : ''}`}
                                onClick={() =>
                                  this.onTableFilterChange(
                                    { target: { value: selected ? '' : option.value } },
                                    'priority'
                                  )
                                }
                              >
                                {option.text}
                              </button>
                            );
                          })}
                        </div>
                      </fieldset>
                      <fieldset className="clubsDsFilterField clubsInvitationsFilterChips">
                        <legend>Participant</legend>
                        <div className="clubsDsFilterChips">
                          {participantOptions.map(option => {
                            const selected = String(filters.participant || '') === String(option.value);
                            return (
                              <button
                                type="button"
                                key={String(option.value)}
                                className={`clubsDsFilterChip ${selected ? 'isSelected' : ''}`}
                                onClick={() =>
                                  this.onTableFilterChange(
                                    { target: { value: selected ? '' : option.value } },
                                    'participant'
                                  )
                                }
                              >
                                {option.text}
                              </button>
                            );
                          })}
                        </div>
                      </fieldset>
                      <fieldset className="clubsDsFilterField clubsInvitationsTimeRange">
                        <legend>Start time</legend>
                        <div>
                          <TimePicker
                            value={this.getFilterTime(filters.startTime?.from || '')}
                            onChange={value => this.setTimeRangeFilter('from', value)}
                            placeholder="From"
                            minuteStep={5}
                            clearable={true}
                            showConfirm={true}
                            size="sm"
                          />
                          <TimePicker
                            value={this.getFilterTime(filters.startTime?.to || '')}
                            onChange={value => this.setTimeRangeFilter('to', value)}
                            placeholder="To"
                            minuteStep={5}
                            clearable={true}
                            showConfirm={true}
                            size="sm"
                            align="right"
                          />
                        </div>
                      </fieldset>
                      {(['startDate', 'finishDate'] as const).map(field => (
                        <div className="clubsDsFilterField clubsDsDateRange" key={field}>
                          <SitDatePicker
                            label={field === 'startDate' ? 'Start date' : 'Finish date'}
                            mode="range"
                            value={{
                              start: this.getFilterDate(filters[field]?.from || ''),
                              end: this.getFilterDate(filters[field]?.to || '')
                            }}
                            onChange={value => this.setDateRangeFilter(field, value)}
                            format="DD-MM-YYYY"
                            placeholder="Select date range"
                            numberOfMonths={2}
                            align="left"
                            clearable={true}
                            syncRangeOnSelect={true}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="clubsDsFilterActions">
                      <button
                        type="button"
                        className="clubsDsBtn clubsDsBtnGhost is-clear"
                        onClick={this.onClearFilterClick}
                      >
                        Clear all
                      </button>
                      <div>
                        <button
                          type="button"
                          className="clubsDsBtn clubsDsBtnGhost"
                          onClick={this.onCancelFilterClick}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="clubsDsBtn clubsDsBtnPrimary is-apply"
                          onClick={this.onApplyFilterClick}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="clubsDsColumns">
              <button
                type="button"
                className="clubsDsBtn clubsDsBtnSecondary"
                onClick={() => this.setState({ isColumnsMenuOpen: !this.state.isColumnsMenuOpen })}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  aria-hidden="true"
                >
                  <path d="M4 5h16v14H4zM9 5v14M15 5v14" />
                </svg>
                Columns <span className="clubsDsBtnBadge">{visibleColumnCount}</span>
              </button>
              {this.state.isColumnsMenuOpen && (
                <div className="clubsDsColumnsMenu">
                  <div className="clubsDsColumnsHead">
                    <span>Show / hide columns</span>
                    <button type="button" onClick={() => this.setHiddenColumns([])}>
                      Reset
                    </button>
                  </div>
                  {invitationColumns.map(column => (
                    <label key={column.key} className={`clubsDsColumnsItem ${column.locked ? 'isLocked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={column.locked || visible(column.key)}
                        disabled={column.locked}
                        onChange={() => this.toggleColumn(column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

            {activeFilters.length > 0 && (
            <div className="clubsDsActiveFilters clubsInvitationsActiveFilters" aria-label="Applied filters">
              {activeFilters.map(filter => (
                <span key={filter.field}>
                  <b>{filter.label}:</b> {filter.value}
                  <button
                    type="button"
                    onClick={() => this.removeAppliedFilter(filter.field)}
                    aria-label={`Remove ${filter.label.toLowerCase()} filter`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button type="button" className="clubsDsActiveFiltersClear" onClick={this.onClearFilterClick}>
                Clear all
              </button>
            </div>
            )}

          {selectedItems.length > 0 && (
            <div className="clubsDsSelectionBar">
              <div className="clubsDsSelectionCount">{selectedItems.length} selected</div>
              <button
                type="button"
                className="clubsDsBtn clubsDsBtnSecondary"
                disabled={!this.isAddToClubButtonActive()}
                onClick={this.onAddToClubClick}
              >
                Add to club
              </button>
              {selectedItems.length === 1 && (
                <button type="button" className="clubsDsBtn clubsDsBtnSecondary" onClick={this.changeStatus}>
                  Change status
                </button>
              )}
              <button type="button" className="clubsDsBtn clubsDsBtnGhost" onClick={this.onUnselectAllClick}>
                Clear
              </button>
            </div>
          )}

          <div className="clubsDsTableCard">
            <div className="clubsDsTableScroll">
              <table className="clubsDsTable">
                <thead>
                  <tr>
                    <th className="clubsDsCheckCell">
                      <SelectAllCheckbox
                        compact
                        isChecked={allOnPageSelected}
                        totalCount={total}
                        currentPage={currentPage}
                        pageItemsCount={items.length}
                        selectedItems={selectedItems}
                        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
                        onSelectAllClick={this.onSelectAllClick}
                        onUnselectAllClick={this.onUnselectAllClick}
                      />
                    </th>
                    <th>Club</th>
                    {visible('student') && <th>Student</th>}
                    {visible('schedule') && <th>Schedule</th>}
                    {visible('status') && (
                      <th>
                        <button
                          type="button"
                          className="clubsDsSort"
                          onClick={() => this.onTableSortClick('invitationStatus')}
                        >
                          Status {sortColumnsName === 'invitationStatus' ? (sortDirection === 'ASC' ? '↑' : '↓') : ''}
                        </button>
                      </th>
                    )}
                    {visible('priority') && <th>Priority</th>}
                    {visible('updated') && (
                      <th>
                        <button
                          type="button"
                          className="clubsDsSort"
                          onClick={() => this.onTableSortClick('updatedAt')}
                        >
                          Updated {sortColumnsName === 'updatedAt' ? (sortDirection === 'ASC' ? '↑' : '↓') : ''}
                        </button>
                      </th>
                    )}
                    {visible('participant') && <th>Participant</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr className="clubsInvitationsEmptyRow">
                      <td colSpan={visibleColumnCount + 1}>
                        <span aria-hidden="true">◎</span>
                        <strong>
                          {isArchiveAwaitingFilters
                            ? 'Choose filters to search the archive'
                            : activeFilters.length > 0
                            ? 'No invitations match these filters'
                            : 'No current club invitations'}
                        </strong>
                        <small>
                          {isArchiveAwaitingFilters
                            ? 'Use a club, student, status or date filter to load only the archived invitations you need.'
                            : activeFilters.length > 0
                            ? 'Try changing or clearing the filters to see more invitations.'
                            : `Invitations for clubs planned in ${academicYearLabel} will appear here.`}
                        </small>
                        {isArchiveAwaitingFilters && (
                          <button
                            type="button"
                            className="clubsDsBtn clubsDsBtnPrimary clubsInvitationsOpenFilters"
                            onClick={() => this.setState({ isShowFilter: true })}
                          >
                            Open filters
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    items.map((message, index) => {
                      const checked = selectedItems.some(item => item.id === message.id);
                      const student = `${message.playerDetailsData.firstName || ''} ${message.playerDetailsData
                        .lastName || ''}`.trim();
                      return (
                        <tr
                          key={message.id}
                          className={previewItem && previewItem.id === message.id ? 'isPreview' : ''}
                          onClick={() => this.setState({ previewItem: message })}
                        >
                          <td className="clubsDsCheckCell" onClick={event => event.stopPropagation()}>
                            <input type="checkbox" checked={checked} onChange={() => this.onItemClick(index)} />
                          </td>
                          <td>
                            <div className="clubsDsClubCell">
                              <span>
                                <strong>{message.clubData.name}</strong>
                                <small>
                                  {formatTableDate(message.clubData.startDate)} –{' '}
                                  {formatTableDate(message.clubData.finishDate)}
                                </small>
                              </span>
                            </div>
                          </td>
                          {visible('student') && (
                            <td>
                              <div className="clubsInvitationsStudent">
                                <span>
                                  {student
                                    ? student
                                        .split(/\s+/)
                                        .map(part => part.charAt(0))
                                        .slice(0, 2)
                                        .join('')
                                    : '?'}
                                </span>
                                <strong>{student || '—'}</strong>
                              </div>
                            </td>
                          )}
                          {visible('schedule') && (
                            <td>
                              {getClubDays(message)} · {getClubStartTimeFromMessage(message)}
                            </td>
                          )}
                          {visible('status') && (
                            <td>
                              <span className={`clubsDsBadge ${getStatusClass(message)}`}>
                                {getClubMessageStatus(message)}
                              </span>
                            </td>
                          )}
                          {visible('priority') && (
                            <td>
                              <span className="clubsInvitationPriority">
                                {typeof message.priority !== 'undefined' ? message.priority : '—'}
                              </span>
                            </td>
                          )}
                          {visible('updated') && <td>{formatTableDate(message.updatedAt)}</td>}
                          {visible('participant') && (
                            <td>
                              <span className={`clubsInvitationParticipant ${message.isParticipant ? 'isYes' : ''}`}>
                                {getIsParticipant(message)}
                              </span>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {!isArchiveAwaitingFilters && <div className="clubsDsFooter">
            <span className="clubsDsPaginationSummary">
              Page {currentPage} of {pageCount}
            </span>
            <button
              type="button"
              className="clubsDsPagerBtn"
              disabled={currentPage <= 1}
              onClick={() => this.setCurrentPageParams(currentPage - 1)}
            >
              Previous
            </button>
            {pageWindowStart > 1 && (
              <>
                <button type="button" className="clubsDsPageBtn" onClick={() => this.setCurrentPageParams(1)}>
                  1
                </button>
                {pageWindowStart > 2 && <span className="clubsDsPaginationEllipsis">...</span>}
              </>
            )}
            {pages.map(pageNumber => (
              <button
                type="button"
                key={pageNumber}
                className={`clubsDsPageBtn ${pageNumber === currentPage ? 'isActive' : ''}`}
                onClick={() => this.setCurrentPageParams(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            {pageWindowEnd < pageCount && (
              <>
                {pageWindowEnd < pageCount - 1 && <span className="clubsDsPaginationEllipsis">...</span>}
                <button type="button" className="clubsDsPageBtn" onClick={() => this.setCurrentPageParams(pageCount)}>
                  {pageCount}
                </button>
              </>
            )}
            <button
              type="button"
              className="clubsDsPagerBtn"
              disabled={currentPage >= pageCount}
              onClick={() => this.setCurrentPageParams(currentPage + 1)}
            >
              Next
            </button>
          </div>}
          </section>
        </div>
        {this.renderInvitationDrawer()}
      </div>
    );
  }

  addStudentsToClub = () => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    const potentialClubsParticipants = selectedItems.map(message => {
      return {
        userId: message.playerDetails.userId,
        permissionId: message.playerDetails.permissionId,
        clubId: message.clubId
      };
    });

    const clubIds = Lazy(selectedItems)
      .uniq('clubId')
      .map(message => message.clubId)
      .toArray();

    this.setState({
      isLoading: true
    });

    BPromise.all(
      clubIds.map(clubId => {
        const clubParticipants = potentialClubsParticipants.filter(participant => participant.clubId === clubId);

        const data = {
          participants: clubParticipants.map(participant => {
            return {
              userId: participant.userId,
              permissionId: participant.permissionId
            };
          })
        };

        return addClubStudents(user, clubId, data);
      })
    ).then(res => {
      this.setState({
        isClubInfoModalOpen: false
      });

      this.setItems();
    });
  };

  clubErrorsModalClose = (): void => {
    this.setState({
      isClubErrorsModalOpen: false
    });
  };

  renderClubErrorsModal(): React.ReactNode {
    const { errors, isClubErrorsModalOpen } = this.state;

    const errorsText = errors.map(error => {
      return `Club name: ${error.name} \nSpaces available: ${error.maxParticipants -
        error.participantsCount} \nStudents you are trying to add: ${error.potentialClubParticipantsCount} \n`;
    });

    return (
      <SimpleModal
        isOpen={isClubErrorsModalOpen}
        title={'Add students to club'}
        body={`There are no sufficient spaces on the clubs: \n\n${errorsText.join(
          '\n'
        )} \nPlease either amend the number of spaces available on the club settings page or deselect some students to match the number of available spaces.`}
        onCloseClick={this.clubErrorsModalClose}
      />
    );
  }

  clubInfoModalClose = (): void => {
    this.setState({
      isClubInfoModalOpen: false
    });
  };

  renderClubInfoModal() {
    const { selectedItems, isClubInfoModalOpen } = this.state;

    const potentialClubsParticipants = selectedItems.map(message => {
      return {
        userId: message.playerDetails.userId,
        permissionId: message.playerDetails.permissionId,
        clubId: message.clubId
      };
    });

    const clubIds = Lazy(selectedItems)
      .uniq('clubId')
      .map(message => message.clubId)
      .toArray();

    const clubSummaries = clubIds.map(clubId => {
      const potentialClubParticipants = potentialClubsParticipants.filter(participant => participant.clubId === clubId);
      const potentialClubParticipantsCount = potentialClubParticipants.length;
      const messageClubData = selectedItems.find(message => message.clubId === clubId);
      const clubData = messageClubData.clubData;

      const { maxParticipants, participants } = clubData;
      const participantsCount = participants.length;

      const clubName = clubData.name;

      const availableSpaces = maxParticipants === 0 ? 'unlimited' : `${maxParticipants - participantsCount}`;

      return { clubId, clubName, availableSpaces, potentialClubParticipantsCount };
    });

    const formatDateTime = (value?: string) => {
      const date = value ? moment(value) : null;
      return date && date.isValid() ? date.format('DD MMM YYYY, HH:mm') : 'Not recorded';
    };

    return (
      <SimpleModal
        isOpen={isClubInfoModalOpen}
        title="Add to club"
        body={
          <div className="clubsInvitationModalSummary">
            <p className="clubsInvitationModalSummary__intro">
              Review the invitation and response details before adding{' '}
              {selectedItems.length === 1 ? 'this student' : 'these students'}.
            </p>
            <div className="clubsInvitationModalSummary__clubs">
              {clubSummaries.map(club => (
                <div key={club.clubId} className="clubsInvitationModalSummary__club">
                  <strong>{club.clubName}</strong>
                  <span>
                    {club.potentialClubParticipantsCount} selected · {club.availableSpaces} spaces available
                  </span>
                </div>
              ))}
            </div>
            <div className="clubsInvitationModalSummary__list">
              {selectedItems.map(message => {
                const student = `${message.playerDetailsData.firstName || ''} ${message.playerDetailsData.lastName ||
                  ''}`.trim();
                const responseUser = getResponseUserText(message);
                const wasUpdated = wasEditedAfterResponse(message) || statusChangedAfterFirstResponse(message);
                return (
                  <article key={message.id} className="clubsInvitationModalSummary__item">
                    <div className="clubsInvitationModalSummary__itemHead">
                      <div>
                        <strong>{student || 'Unknown student'}</strong>
                        <span>{message.clubData.name}</span>
                      </div>
                      <span className={`clubsDsBadge ${this.getInvitationStatusClass(message)}`}>
                        {getClubMessageStatus(message)}
                      </span>
                    </div>
                    <dl>
                      <div>
                        <dt>Status set</dt>
                        <dd>{formatDateTime(message.invitationStatusSetAt)}</dd>
                      </div>
                      <div>
                        <dt>Invitation sent by</dt>
                        <dd>{message.sender?.fullName || 'Not recorded'}</dd>
                      </div>
                      <div>
                        <dt>Responded / changed by</dt>
                        <dd>{responseUser || 'Not recorded'}</dd>
                      </div>
                      <div>
                        <dt>Last updated</dt>
                        <dd>{formatDateTime(message.updatedAt)}</dd>
                      </div>
                      <div>
                        <dt>Updated after response</dt>
                        <dd>{wasUpdated ? 'Yes' : 'No'}</dd>
                      </div>
                    </dl>
                  </article>
                );
              })}
            </div>
          </div>
        }
        buttonText="Add to club"
        onButtonClick={this.addStudentsToClub}
        onCloseClick={this.clubInfoModalClose}
        customClass="clubsInvitationActionModal"
        bodyClassName="clubsInvitationActionModal__body"
        dialogStyle={{ maxWidth: 720 }}
        isBodyBold={false}
      />
    );
  }

  onChangeStatusSubmitClick = (data: { invitationStatus: string; priority?: number }): void => {
    const { user } = this.props;
    const { selectedItems } = this.state;
    const message = selectedItems[0];
    const { id: messageId, clubId } = message;

    const { invitationStatus, priority } = data;
    const isInvitationStatusAccepted = invitationStatus === CLUB_PARTICIPATION_MESSAGE_INVITATION_STATUS.ACCEPTED;

    const dataCopy = {
      invitationStatus: data.invitationStatus,
      priority: Number(priority)
    };

    if (!isInvitationStatusAccepted || dataCopy.priority === 0) {
      delete dataCopy.priority;
    }

    updateClubMessage(user, clubId, messageId, dataCopy).then(messageUpdated => {
      this.setState({
        isChangeStatusModalOpen: false,
        selectedItems: [],
        isSelectAllChecked: false
      });
      this.setItems();
    });
  };

  renderChangeStatusModal(): React.ReactNode {
    const { isChangeStatusModalOpen, selectedItems } = this.state;

    const message = selectedItems[0];
    if (!message) return null;

    const student = `${message.playerDetailsData.firstName || ''} ${message.playerDetailsData.lastName || ''}`.trim();
    const responseUser = getResponseUserText(message);
    const wasUpdated = wasEditedAfterResponse(message) || statusChangedAfterFirstResponse(message);
    const statusSetAt = message.invitationStatusSetAt && moment(message.invitationStatusSetAt);
    const updatedAt = message.updatedAt && moment(message.updatedAt);

    return (
      <SimpleModal
        isOpen={isChangeStatusModalOpen}
        title="Change invitation status"
        customClass="clubsInvitationActionModal"
        bodyClassName="clubsInvitationActionModal__body"
        dialogStyle={{ maxWidth: 620 }}
        showFooter={false}
        isBodyBold={false}
        onCloseClick={this.onChangeStatusCancelClick}
      >
        <div className="clubsInvitationStatusContext">
          <div className="clubsInvitationStatusContext__head">
            <div>
              <strong>{student || 'Unknown student'}</strong>
              <span>{message.clubData.name}</span>
            </div>
            <span className={`clubsDsBadge ${this.getInvitationStatusClass(message)}`}>
              {getClubMessageStatus(message)}
            </span>
          </div>
          <dl>
            <div>
              <dt>Original status</dt>
              <dd>{getClubMessageStatus(message)}</dd>
            </div>
            <div>
              <dt>Status set</dt>
              <dd>
                {statusSetAt && statusSetAt.isValid() ? statusSetAt.format('DD MMM YYYY, HH:mm') : 'Not recorded'}
              </dd>
            </div>
            <div>
              <dt>Invitation sent by</dt>
              <dd>{message.sender?.fullName || 'Not recorded'}</dd>
            </div>
            <div>
              <dt>Responded / changed by</dt>
              <dd>{responseUser || 'Not recorded'}</dd>
            </div>
            <div>
              <dt>Last updated</dt>
              <dd>{updatedAt && updatedAt.isValid() ? updatedAt.format('DD MMM YYYY, HH:mm') : 'Not recorded'}</dd>
            </div>
            <div>
              <dt>Updated after response</dt>
              <dd>{wasUpdated ? 'Yes' : 'No'}</dd>
            </div>
          </dl>
        </div>
        <ClubMessageStatusForm
          message={message}
          onSubmit={this.onChangeStatusSubmitClick}
          onCancel={this.onChangeStatusCancelClick}
        />
      </SimpleModal>
    );
  }

  renderInvitationDrawer(): React.ReactNode {
    const message = this.state.previewItem;
    if (!message) return null;

    const student = `${message.playerDetailsData.firstName || ''} ${message.playerDetailsData.lastName || ''}`.trim();
    const respondedBy = getResponseUserText(message);
    const respondedAt = getRespondedAtText(message);
    const wasUpdated = wasEditedAfterResponse(message) || statusChangedAfterFirstResponse(message);
    const initials = student
      ? student
          .split(/\s+/)
          .map(part => part.charAt(0))
          .slice(0, 2)
          .join('')
      : '?';
    const status = String(message.invitationStatus || '').toUpperCase();
    const statusClass =
      status === 'ACCEPTED'
        ? 'isSuccess'
        : status === 'REJECTED' || status === 'OUTDATED'
        ? 'isDanger'
        : status.indexOf('WAITING_LIST') !== -1
        ? 'isWarning'
        : 'isNeutral';
    const formatDateTime = (value: string | undefined) => {
      if (!value) return '—';
      const date = moment(value);
      return date.isValid() ? date.format('DD MMM YYYY, HH:mm') : '—';
    };
    const formatDate = (value: string | undefined) => {
      if (!value) return '—';
      const date = moment(value);
      return date.isValid() ? date.format('DD MMM YYYY') : '—';
    };
    const closeDrawer = () => this.setState({ previewItem: undefined });

    return (
      <>
        <div className="clubsDsDrawerScrim" onClick={closeDrawer} aria-hidden="true" />
        <aside
          className="clubsDsDrawer clubsInvitationsDrawer"
          role="dialog"
          aria-modal="true"
          aria-label="Invitation details"
        >
          <header className="clubsInvitationDrawer__head">
            <span className="clubsInvitationDrawer__avatar" aria-hidden="true">
              {initials}
            </span>
            <div className="clubsInvitationDrawer__identity">
              <h2>{student || 'Invitation details'}</h2>
              <div>
                <span>{message.clubData.name}</span>
                <i />
                <span className={`clubsDsBadge ${statusClass}`}>{getClubMessageStatus(message)}</span>
                {wasUpdated && <span className="clubsInvitationDrawer__updated">Updated response</span>}
              </div>
            </div>
            <button
              type="button"
              className="clubsInvitationDrawer__close"
              aria-label="Close invitation details"
              onClick={closeDrawer}
            >
              ×
            </button>
          </header>
          <div className="clubsInvitationDrawer__body">
            <section className="clubsInvitationDrawer__section">
              <h3>Invitation</h3>
              <dl className="clubsInvitationDrawer__details">
                <div>
                  <dt>Club</dt>
                  <dd>{message.clubData.name}</dd>
                </div>
                <div>
                  <dt>Schedule</dt>
                  <dd>
                    {getClubDays(message)} at {getClubStartTimeFromMessage(message)}
                  </dd>
                </div>
                <div>
                  <dt>Club dates</dt>
                  <dd>
                    {formatDate(message.clubData.startDate)} – {formatDate(message.clubData.finishDate)}
                  </dd>
                </div>
                <div>
                  <dt>Invitation sent</dt>
                  <dd>{formatDateTime(message.createdAt)}</dd>
                </div>
                <div>
                  <dt>Last updated</dt>
                  <dd>{formatDateTime(message.updatedAt)}</dd>
                </div>
                <div>
                  <dt>Participant</dt>
                  <dd>{getIsParticipant(message)}</dd>
                </div>
              </dl>
            </section>

            <section className="clubsInvitationDrawer__section clubsInvitationDrawer__response">
              <div className="clubsInvitationDrawer__sectionHead">
                <div>
                  <h3>Booking response</h3>
                  <p>Response details and submitted form answers.</p>
                </div>
                {wasUpdated && <span className="clubsInvitationDrawer__updated">Updated after response</span>}
              </div>
              {respondedAt ? (
                <>
                  <div className="clubsInvitationDrawer__responseMeta">
                    <div>
                      <span>Responded by</span>
                      <strong>{respondedBy || 'Unknown user'}</strong>
                    </div>
                    <div>
                      <span>Responded at</span>
                      <strong>{respondedAt}</strong>
                    </div>
                  </div>
                  <div className="clubsInvitationDrawer__answers">
                    <ClubConfirmationRequestSummary message={message} />
                  </div>
                </>
              ) : (
                <div className="clubsInvitationDrawer__empty">
                  <span aria-hidden="true">?</span>
                  <strong>No response submitted</strong>
                  <p>This invitation is still awaiting a response.</p>
                </div>
              )}
            </section>
          </div>
          <footer className="clubsInvitationDrawer__foot">
            <button
              type="button"
              className="clubsDsBtn clubsDsBtnSecondary"
              onClick={() =>
                this.setState({
                  selectedItems: [message],
                  previewItem: undefined,
                  isChangeStatusModalOpen: true
                })
              }
            >
              Change status
            </button>
            {!message.isParticipant && (
              <button
                type="button"
                className="clubsDsBtn clubsDsBtnPrimary"
                onClick={() =>
                  this.setState({ selectedItems: [message], previewItem: undefined }, this.onAddToClubClick)
                }
              >
                Add to club
              </button>
            )}
          </footer>
        </aside>
      </>
    );
  }

  getInvitationStatusClass(message: ClubMessage): string {
    const status = String(message.invitationStatus || '').toUpperCase();
    if (status === 'ACCEPTED') return 'isSuccess';
    if (status === 'REJECTED' || status === 'OUTDATED') return 'isDanger';
    if (status.indexOf('WAITING_LIST') !== -1) return 'isWarning';
    return 'isNeutral';
  }

  render() {
    const { isLoading, isClubErrorsModalOpen, isChangeStatusModalOpen, isClubInfoModalOpen } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isClubErrorsModalOpen || isChangeStatusModalOpen || isClubInfoModalOpen ? 'modal-open' : '';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderClubErrorsModal()}
        {this.renderClubInfoModal()}
        {this.renderChangeStatusModal()}
        {this.renderSchoolClubMessagesTable()}
      </div>
    );
  }
}
